// Quick-action integrations (Aircall / WhatsApp / mailto / maps). These are pure
// helpers: each performs its side-effect (open an aircall:// / wa.me / mailto: /
// maps target) and reports the outcome, but they do NOT touch the toast/i18n
// layer. The calling component decides how to surface a failure (see the
// `useQuickActions` hook). Keeping this file free of project UI deps means it
// stays trivially testable and won't break if the toast or i18n wiring changes.

// Default region for national-format numbers (France). Already-international
// numbers (+1, +84, 0033…) are preserved so cross-border clients still work.
const DEFAULT_COUNTRY_CODE = '33'

// Why a quick action could not run. The component maps this to a user-facing
// message (the AC "no contact on file" branches live in the errors namespace).
export type IntegrationFailureReason = 'no-phone' | 'no-email' | 'no-address'

// Discriminated result: `ok` actions carry nothing, failures carry the reason.
export type IntegrationResult =
  | { ok: true }
  | { ok: false; reason: IntegrationFailureReason }

// Normalise a raw phone string to E.164 (e.g. "+33612345678"). Handles French
// national format ("06…", "05 48 67 18 91"), the "+33 (0)6…" trunk artifact,
// the "00" international prefix, and already-international "+…" numbers. Returns
// null when there is nothing dialable.
export function toE164(phone: string | null | undefined): string | null {
  if (!phone) {
    return null
  }

  // Drop the "(0)" trunk indicator (common in "+33 (0)6 12 34 56 78").
  const cleaned = phone.replace(/\(0\)/g, '')
  const startsInternational = cleaned.trimStart().startsWith('+')
  const digits = cleaned.replace(/[^\d]/g, '')

  if (digits === '') {
    return null
  }

  // Already international: keep the country code as-is.
  if (startsInternational) {
    return `+${digits}`
  }

  // "00" is the international call prefix → swap for "+".
  if (digits.startsWith('00')) {
    return `+${digits.slice(2)}`
  }

  // National format ("0…") → default country, dropping the trunk 0.
  if (digits.startsWith('0')) {
    return `+${DEFAULT_COUNTRY_CODE}${digits.slice(1)}`
  }

  // Bare digits without trunk/prefix are assumed to already carry a country code.
  return `+${digits}`
}

export function callClient(
  phone: string | null | undefined
): IntegrationResult {
  const e164 = toE164(phone)
  if (!e164) {
    return { ok: false, reason: 'no-phone' }
  }
  // Aircall's custom URL scheme opens the Aircall app directly. Use `to`, not
  // `number`; otherwise Workspace treats the path/query as a search string.
  const link = document.createElement('a')
  link.href = `aircall://${encodeURIComponent(e164)}`
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  link.remove()
  return { ok: true }
}

export function whatsappClient(
  phone: string | null | undefined
): IntegrationResult {
  const e164 = toE164(phone)
  if (!e164) {
    return { ok: false, reason: 'no-phone' }
  }
  // wa.me expects the international number without the leading "+".
  window.open(`https://wa.me/${e164.slice(1)}`, '_blank', 'noopener')
  return { ok: true }
}

export function emailClient(
  email: string | null | undefined
): IntegrationResult {
  if (!email) {
    return { ok: false, reason: 'no-email' }
  }
  window.open(`mailto:${email}`, '_self')
  return { ok: true }
}

export type NavigateDestination = {
  address?: string | null
  latitude?: number | null
  longitude?: number | null
}

// Opens turn-by-turn directions to an intervention site. Prefers stored
// coordinates when both are present; otherwise falls back to the text address.
export function navigateToAddress(
  destination: NavigateDestination | string | null | undefined
): IntegrationResult {
  const address =
    typeof destination === 'string' ? destination : destination?.address
  const latitude =
    typeof destination === 'object' && destination
      ? destination.latitude
      : undefined
  const longitude =
    typeof destination === 'object' && destination
      ? destination.longitude
      : undefined

  const hasCoords =
    typeof latitude === 'number' &&
    typeof longitude === 'number' &&
    !Number.isNaN(latitude) &&
    !Number.isNaN(longitude)
  const mapsDestination = hasCoords
    ? `${latitude},${longitude}`
    : address?.trim()

  if (!mapsDestination) {
    return { ok: false, reason: 'no-address' }
  }

  window.open(
    `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapsDestination)}`,
    '_blank',
    'noopener'
  )
  return { ok: true }
}
